/**
 * LoginPage — passkey login, plus an email link for organisations that allow
 * it (/api/auth/email-login/request; the answer is the same for every
 * address). If the device has no passkey, the user is pointed at
 * 'Passkey kwijt?' to request a reset.
 */

import { type FormEvent, useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { carmenAuth } from "@/lib/carmenAuth";
import { API_URL } from "@/lib/env";
import { authenticatePasskey, completeLogin } from "@/lib/passkey";

function getRedirectTarget(search: URLSearchParams): string {
  const redirect = search.get("redirect");
  // Accept only same-origin absolute paths. Reject protocol-relative (//evil)
  // and backslash-prefixed paths that some browsers normalise to //.
  if (
    redirect &&
    redirect.startsWith("/") &&
    !redirect.startsWith("//") &&
    !redirect.startsWith("/\\")
  ) {
    return redirect;
  }
  return "/download";
}

const PANEL_CLASS =
  "rounded-[32px] border border-[rgba(10,52,96,0.10)] bg-white/92 p-8 shadow-[0_24px_64px_rgba(10,52,96,0.10)] backdrop-blur-xl sm:p-10";
const FIELD_CLASS =
  "mt-2 w-full rounded-[18px] border border-[rgba(12,63,107,0.18)] bg-white px-4 py-3.5 text-[15px] text-[#111111] shadow-[inset_0_1px_0_rgba(255,255,255,0.5)] outline-none transition-all duration-200 placeholder:text-[rgba(125,156,180,0.95)] focus:border-[rgba(12,63,107,0.46)] focus:ring-4 focus:ring-[rgba(238,244,248,0.9)]";
const PRIMARY_BUTTON_CLASS =
  "w-full rounded-full bg-[var(--cs-title-navy)] px-5 py-3.5 text-[15px] font-semibold text-white shadow-[0_16px_34px_rgba(10,52,96,0.18)] transition-colors duration-200 hover:bg-[var(--cs-title-navy)] disabled:cursor-not-allowed disabled:opacity-50";
const SECONDARY_BUTTON_CLASS =
  "w-full rounded-full border border-[rgba(12,63,107,0.30)] bg-white px-5 py-3.5 text-[15px] font-semibold text-[var(--cs-title-navy)] transition-colors duration-200 hover:bg-[rgba(238,244,248,0.9)] disabled:cursor-not-allowed disabled:opacity-50";

export function LoginPage() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [passkeyHint, setPasskeyHint] = useState("");
  const [loading, setLoading] = useState(false);
  const [linkSentTo, setLinkSentTo] = useState("");

  // Redirect away if already logged in (check once on mount)
  useEffect(() => {
    void carmenAuth.getSession().then((session) => {
      if (session) nav(getRedirectTarget(searchParams));
    });
  }, [nav, searchParams]);

  const handlePasskey = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError("");
      setPasskeyHint("");
      setLoading(true);
      try {
        const result = await authenticatePasskey(email.trim() || undefined);
        await completeLogin(result.magic_link_token);
        nav(getRedirectTarget(searchParams));
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Inloggen mislukt.";
        setError(msg);
        // NotAllowedError = "no passkey for this site on this device" or user
        // dismissed the prompt.
        if (err instanceof Error && /NotAllowed/i.test(err.name + err.message)) {
          setPasskeyHint(
            "Geen passkey op dit toestel? Log in op een toestel met uw passkey, of gebruik 'Passkey kwijt?' hieronder.",
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [email, nav, searchParams],
  );

  const handleEmailLink = useCallback(async () => {
    const address = email.trim();
    setError("");
    setPasskeyHint("");
    if (!address) {
      setError("Vul uw e-mailadres in.");
      return;
    }
    setLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/auth/email-login/request`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: address }),
      });
      if (!resp.ok) {
        const body = (await resp.json().catch(() => ({}))) as { detail?: unknown };
        throw new Error(typeof body.detail === "string" ? body.detail : `Fout ${resp.status}`);
      }
      setLinkSentTo(address);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Inloglink aanvragen mislukt.");
    } finally {
      setLoading(false);
    }
  }, [email]);

  return (
    <div className="cs-marketing min-h-screen bg-[linear-gradient(180deg,#fcfbf7_0%,#ffffff_22%,#f3f9fd_100%)] px-4 py-12 sm:px-6 sm:py-16">
      <div className="mx-auto w-full max-w-md space-y-6">
        <div className={PANEL_CLASS}>
          <div className="mb-8 text-center">
            <h1 className="text-[38px] font-semibold tracking-[-0.05em] text-[var(--cs-title-navy)]">
              Inloggen
            </h1>
          </div>

          {error ? (
            <div className="mb-5 rounded-[18px] border border-[#F3C7CD] bg-[#FFF4F6] p-4 text-sm text-[#B42318]">
              {error}
            </div>
          ) : null}

          <form onSubmit={handlePasskey} className="space-y-5">
            <div>
              <label
                htmlFor="email"
                className="block text-[15px] font-medium text-[var(--cs-title-navy)]"
              >
                E-mailadres
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username webauthn"
                placeholder="u@voorbeeld.be"
                className={FIELD_CLASS}
              />
            </div>

            <button type="submit" disabled={loading} className={PRIMARY_BUTTON_CLASS}>
              {loading ? "Bezig…" : "Inloggen met passkey"}
            </button>
            <button
              type="button"
              onClick={() => void handleEmailLink()}
              disabled={loading}
              className={SECONDARY_BUTTON_CLASS}
            >
              Inloggen met e-maillink
            </button>
          </form>

          {linkSentTo ? (
            <p className="mt-3 text-center text-sm text-[rgba(95,131,160,0.96)]" aria-live="polite">
              Als uw organisatie inloggen via e-mail toestaat, ontvangt u een link op {linkSentTo}.
              Open die binnen 10 minuten in de browser waarin u wilt inloggen.
            </p>
          ) : null}

          {passkeyHint ? (
            <p className="mt-3 text-center text-sm text-[rgba(95,131,160,0.96)]">{passkeyHint}</p>
          ) : null}

          <div className="mt-6 space-y-1 text-center text-sm">
            <div className="text-[rgba(95,131,160,0.96)]">
              Geen account?{" "}
              <a
                href="/signup"
                className="text-[var(--cs-title-navy)] transition-colors hover:text-[var(--cs-title-navy)]"
              >
                Registreer
              </a>
            </div>
            <div className="text-[rgba(95,131,160,0.96)]">
              <a
                href="/recover"
                className="text-[var(--cs-title-navy)] transition-colors hover:text-[var(--cs-title-navy)]"
              >
                Passkey kwijt?
              </a>
            </div>
          </div>
        </div>

        <div className="text-center text-xs text-[rgba(125,156,180,0.96)]">
          Door in te loggen gaat u akkoord met ons{" "}
          <a
            href="/privacy"
            className="underline decoration-[rgba(12,63,107,0.20)] underline-offset-4 hover:text-[var(--cs-title-navy)]"
          >
            privacybeleid
          </a>{" "}
          en de{" "}
          <a
            href="/terms"
            className="underline decoration-[rgba(12,63,107,0.20)] underline-offset-4 hover:text-[var(--cs-title-navy)]"
          >
            gebruiksvoorwaarden
          </a>
          .
        </div>
      </div>
    </div>
  );
}
